import React from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui';
import { JobDetails } from './JobDetails';
import { useAdminJobs } from '@/hooks/queries';
import { apiFetch } from '@/lib/api';

export function JobsList() {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(15);
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [detailJobId, setDetailJobId] = React.useState<string | null>(null);

  const queryFilters = { page, pageSize, ...filters };
  const { data, isLoading: loading, refetch } = useAdminJobs(queryFilters);
  const items = data?.items || [];
  const total = data?.pagination?.total || 0;

  const columns = [
    { key: 'id', title: 'ID' },
    { key: 'type', title: 'Type' },
    { key: 'status', title: 'Status' },
    { key: 'attempt', title: 'Attempt', align: 'right' as const },
    { key: 'priority', title: 'Priority', align: 'right' as const },
    { key: 'queue', title: 'Queue' },
    { key: 'organization_id', title: 'Org' },
    { key: 'run_at', title: 'Run at', render: (v: string) => new Date(v).toLocaleString() },
    { key: 'started_at', title: 'Started', render: (v: string) => (v ? new Date(v).toLocaleString() : '—') },
    { key: 'finished_at', title: 'Finished', render: (v: string) => (v ? new Date(v).toLocaleString() : '—') },
    {
      key: 'actions',
      title: 'Actions',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setDetailJobId(record.id)}>
            View
          </Button>
          {record.status === 'failed' && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await apiFetch(`/api/admin/jobs/${record.id}/retry`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resetAttempts: true }) });
                refetch();
              }}
            >
              Retry
            </Button>
          )}
          {record.status !== 'completed' && record.status !== 'failed' && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await apiFetch(`/api/admin/jobs/${record.id}/cancel`, { method: 'POST' });
                refetch();
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      )
    }
  ];

  const filterOptions = [
    { key: 'status', label: 'Status', options: [
      { label: 'Queued', value: 'queued' },
      { label: 'Processing', value: 'processing' },
      { label: 'Completed', value: 'completed' },
      { label: 'Failed', value: 'failed' },
    ]},
    { key: 'type', label: 'Type', options: [
      { label: 'All', value: '' },
      { label: 'org_profile_research', value: 'org_profile_research' },
      { label: 'gallery_image_analysis', value: 'gallery_image_analysis' },
      { label: 'embed_doc', value: 'embed_doc' },
      { label: 'drafts_ttl_cleanup', value: 'drafts_ttl_cleanup' },
    ]},
    { key: 'queue', label: 'Queue', options: [ { label: 'default', value: 'default' }, { label: 'system', value: 'system' } ] },
  ];

  return (
    <PageContainer>
      <PageHeader title="Jobs" description="Server-side jobs with filters and pagination." />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        searchable={false}
        filters={filterOptions}
        serverSide={true}
        onFilter={(f) => { setFilters(f); setPage(1); }}
        onPaginate={(p, ps) => { setPage(p); setPageSize(ps); }}
        totalItems={total}
      />
      {detailJobId && (
        <JobDetails jobId={detailJobId} onClose={() => setDetailJobId(null)} />
      )}
    </PageContainer>
  );
}


